import React, { useCallback, useMemo, useState } from 'react';
import { Loader2, Package, Plus, ShoppingBag, XIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useAssociablePurchaseOrdersSearch } from 'components/logistics/components/crossDockPurchaseOrders/useAssociablePurchaseOrdersSearch';
import {
    formatCrossDockPurchaseOrderPoMapLabel,
    getCrossDockPurchaseOrderPoMapHref,
    getCrossDockPurchaseOrderPoMapIdLabel,
    getCrossDockPurchaseOrderPoMapSelectionKey,
    isPackagingCrossDockPurchaseOrderPoMap,
} from 'components/logistics/components/crossDockPurchaseOrders/utils/crossDockPurchaseOrderUtils';
import { Badge } from 'shadcn/components/ui/Badge';
import { Button } from 'shadcn/components/ui/Button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from 'shadcn/components/ui/Command';
import { Label } from 'shadcn/components/ui/Label';
import { Popover, PopoverContent, PopoverTrigger } from 'shadcn/components/ui/Popover';
import { cn } from 'shadcn/lib/utils';

const labelClassName = 'text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground';

function PoTypeIcon({ className, poMap }) {
    const Icon = isPackagingCrossDockPurchaseOrderPoMap(poMap) ? Package : ShoppingBag;
    return <Icon className={cn('h-4 w-4 shrink-0', className)} aria-hidden="true" />;
}

/**
 * @param {Object} props
 * @param {Array<Object>} [props.poMaps]
 * @param {(next: Array<Object>) => void} [props.onPoMapsChange]
 * @param {boolean} [props.readOnly]
 * @param {boolean} [props.showValidation]
 * @param {string} [props.errorMessage]
 */
export function AssociatedPurchaseOrdersField({
    errorMessage = '',
    onPoMapsChange,
    poMaps = [],
    readOnly = false,
    showValidation = false,
}) {
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const selectedKeys = useMemo(
        () => new Set((poMaps ?? []).map(getCrossDockPurchaseOrderPoMapSelectionKey).filter(Boolean)),
        [poMaps],
    );

    const {
        data: searchRows = [],
        isDebouncing = false,
        isFetching,
        isLoading,
    } = useAssociablePurchaseOrdersSearch(searchTerm, {
        enabled: !readOnly && open,
    });

    const availableRows = useMemo(
        () =>
            (searchRows ?? []).filter((row) => {
                const key = getCrossDockPurchaseOrderPoMapSelectionKey(row);
                return key && !selectedKeys.has(key);
            }),
        [searchRows, selectedKeys],
    );

    const isSearching = isDebouncing || isLoading || isFetching;

    const handleOpenChange = useCallback((nextOpen) => {
        setOpen(nextOpen);
        if (!nextOpen) {
            setSearchTerm('');
        }
    }, []);

    const handleSelect = useCallback(
        (row) => {
            if (typeof onPoMapsChange !== 'function') return;
            const key = getCrossDockPurchaseOrderPoMapSelectionKey(row);
            if (!key || selectedKeys.has(key)) return;

            onPoMapsChange([
                ...(poMaps ?? []),
                {
                    poType: row.poType,
                    purchaseOrderId: row.purchaseOrderId ?? null,
                    packagingPurchaseOrderId: row.packagingPurchaseOrderId ?? null,
                    vendorName: row.vendorName ?? null,
                },
            ]);
            setOpen(false);
            setSearchTerm('');
        },
        [onPoMapsChange, poMaps, selectedKeys],
    );

    const handleRemove = useCallback(
        (poMapToRemove) => {
            if (typeof onPoMapsChange !== 'function') return;
            const removeKey = getCrossDockPurchaseOrderPoMapSelectionKey(poMapToRemove);
            onPoMapsChange(
                (poMaps ?? []).filter((poMap) => getCrossDockPurchaseOrderPoMapSelectionKey(poMap) !== removeKey),
            );
        },
        [onPoMapsChange, poMaps],
    );

    const showError = showValidation === true && Boolean(errorMessage);
    const trimmedSearch = String(searchTerm ?? '').trim();

    return (
        <div className="flex flex-col gap-2 xl:col-span-12">
            <Label className={labelClassName}>
                Associated PO&apos;s{readOnly ? null : <span className="text-destructive"> *</span>}
            </Label>
            <div
                className={cn(
                    'flex min-h-10 w-full flex-wrap items-center gap-2 rounded-md border border-input bg-background px-2 py-1.5',
                    showError && 'border-destructive',
                )}
            >
                {(poMaps ?? []).map((poMap) => {
                    const label = formatCrossDockPurchaseOrderPoMapLabel(poMap);
                    const key = getCrossDockPurchaseOrderPoMapSelectionKey(poMap) ?? label;
                    const href = getCrossDockPurchaseOrderPoMapHref(poMap);

                    return (
                        <Badge key={key} variant="secondary" className="max-w-full gap-1.5 font-medium text-foreground">
                            <PoTypeIcon poMap={poMap} />
                            {readOnly && href ? (
                                <Link to={href} className="truncate text-primary underline-offset-4 hover:underline">
                                    {label}
                                </Link>
                            ) : (
                                <span className="truncate">{label}</span>
                            )}
                            {!readOnly ? (
                                <button
                                    type="button"
                                    className="rounded-sm opacity-70 transition-opacity hover:opacity-100"
                                    aria-label={`Remove ${label}`}
                                    onClick={() => handleRemove(poMap)}
                                >
                                    <XIcon className="h-3.5 w-3.5" />
                                </button>
                            ) : null}
                        </Badge>
                    );
                })}

                {!readOnly ? (
                    <Popover open={open} onOpenChange={handleOpenChange}>
                        <PopoverTrigger asChild>
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                className="h-7 w-7 shrink-0"
                                aria-label="Add associated purchase order"
                            >
                                <Plus className="h-4 w-4" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-[360px] p-0">
                            <Command shouldFilter={false}>
                                <CommandInput
                                    placeholder="Type a PO number"
                                    value={searchTerm}
                                    onValueChange={setSearchTerm}
                                />
                                <CommandList>
                                    {trimmedSearch.length < 1 && <CommandEmpty>Type a PO number</CommandEmpty>}
                                    {trimmedSearch.length >= 1 && isSearching && (
                                        <div className="flex items-center justify-center gap-2 py-6 text-sm text-muted-foreground">
                                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                                            Searching…
                                        </div>
                                    )}
                                    {trimmedSearch.length >= 1 && !isSearching && availableRows.length === 0 && (
                                        <CommandEmpty>No results found.</CommandEmpty>
                                    )}
                                    {trimmedSearch.length >= 1 && !isSearching && availableRows.length > 0 && (
                                        <CommandGroup>
                                            {availableRows.map((row) => {
                                                const idLabel = getCrossDockPurchaseOrderPoMapIdLabel(row);
                                                const vendorName = String(row.vendorName ?? '').trim();
                                                const optionKey = getCrossDockPurchaseOrderPoMapSelectionKey(row);
                                                return (
                                                    <CommandItem
                                                        key={optionKey}
                                                        value={optionKey}
                                                        onSelect={() => handleSelect(row)}
                                                    >
                                                        <PoTypeIcon poMap={row} className="mr-2" />
                                                        <span className="shrink-0 font-medium">{idLabel}</span>
                                                        {vendorName ? (
                                                            <span
                                                                className="ml-2 min-w-0 truncate text-muted-foreground"
                                                                title={vendorName}
                                                            >
                                                                {vendorName}
                                                            </span>
                                                        ) : null}
                                                    </CommandItem>
                                                );
                                            })}
                                        </CommandGroup>
                                    )}
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>
                ) : null}
            </div>
            {showError ? <p className="text-xs text-destructive">{errorMessage}</p> : null}
        </div>
    );
}

AssociatedPurchaseOrdersField.displayName = 'AssociatedPurchaseOrdersField';
